import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { bool, object, ref, string } from "yup";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import PhoneNumber from "components/PhoneNumber";
import Switch from "components/Switch";
import { routes } from "constants/routes";
import { TERMS_OF_SERVICE, VEHICLE_NUMBER_TYPES } from "constants/signup";
import { RESPONSE_STATUS } from "constants/status";
import { showErrorMessage } from "helpers/toastMessage";
import { AddressInfo } from "models/User";
import { checkEmailExistAPI, createInvitedUserAPI, getUserByFmcsaAPI } from "services/api/user";
import { AppDispatch } from "store";
import { loginUser } from "store/auth/actions";
import { selectOnboardingInformation } from "store/user/selector";
import styles from "./style.module.scss";

interface IConnectCarrierForm {
  identifyNumber: string;
  email: string;
  owner_first_name: string;
  owner_last_name: string;
  owner_phone_number_cell: string;
  company_name: string;
  address: AddressInfo;
  password: string;
  confirm_password: string;
  agreement: boolean;
  receive_message: boolean;
}

interface IProps {
  handleNext: (paymentProfileId: string) => void;
}

type CreatedInvitedUser = {
  profiles?: {
    payment?: {
      id?: string;
    };
  };
};

const carrierFormSchema = object<Partial<IConnectCarrierForm>>({
  identifyNumber: string().required("This field is required"),
  email: string().required("This field is required"),
  owner_first_name: string().required("This field is required"),
  owner_last_name: string().required("This field is required"),
  owner_phone_number_cell: string().required("This field is required"),
  company_name: string().required("This field is required"),
  address: object().required("This field is required"),
  password: string()
    .required("This field is required")
    .min(8, "Password must be at least 8 characters"),
  confirm_password: string()
    .oneOf([ref("password")], "Passwords must match")
    .required("This field is required"),
  agreement: bool().oneOf([true], "You must accept the terms of service")
});

export default function InvitedCarrierInfo({ handleNext }: Readonly<IProps>) {
  const onboardingInformation = useSelector(selectOnboardingInformation);
  const {
    handleSubmit,
    setValue,
    register,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<Partial<IConnectCarrierForm>>({
    resolver: yupResolver(carrierFormSchema),
    defaultValues: {
      email: onboardingInformation.email
    }
  });

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { inviteToken } = useParams();
  const businessAddress = watch("address");
  const userEmail = watch("email");
  const [selectedCarrierType, setSelectedCarrierType] = useState(VEHICLE_NUMBER_TYPES[0]);
  const [isDuplicatedEmail, setIsDuplicatedEmail] = useState(false);

  function goBack() {
    navigate(routes.SIGNIN);
  }

  const getFMCSAInfo = async () => {
    try {
      const { data: fmcsaData } = await getUserByFmcsaAPI({
        [selectedCarrierType.value]: getValues("identifyNumber")
      });

      if (fmcsaData) {
        const { address, city, country, state, zip, dba, phone } = fmcsaData;
        const businessAddress: AddressInfo = {
          address,
          city,
          country,
          state,
          zip
        };
        setValue("company_name", dba);
        setValue("address", businessAddress);
        // Normalize phone to 10 digits (strip non-digits and remove leading "1" country code if present)
        const normalizedPhone = phone?.replace(/\D/g, "").replace(/^1(\d{10})$/, "$1") ?? "";
        setValue("owner_phone_number_cell", normalizedPhone);
      }
    } catch (err) {
      setValue("company_name", "");
    }
  };

  const checkEmailExist = async (email?: string) => {
    if (email) {
      try {
        const response = await checkEmailExistAPI(email);
        if (response.data.status === RESPONSE_STATUS.FAILURE) setIsDuplicatedEmail(true);
        else setIsDuplicatedEmail(false);
      } catch (err: any) {
        setIsDuplicatedEmail(false);
      }
    }
  };

  const handleCreateCarrier = async (data: IConnectCarrierForm) => {
    try {
      const { data: createdUser } = await createInvitedUserAPI(inviteToken ?? "", {
        contact_name: `${data.owner_first_name} ${data.owner_last_name}`,
        contact_phone_number: data.owner_phone_number_cell,
        address_country: data.address.country,
        address_street_one: data.address.address,
        address_state: data.address.state,
        address_city: data.address.city,
        mc: selectedCarrierType.value === "mc" ? data.identifyNumber : "",
        dot: selectedCarrierType.value === "dot" ? data.identifyNumber : "",
        contact_email: data.email,
        user_type: "carrier",
        company_name: data.company_name,
        address_zip: data.address.zip,
        email: data.email,
        enable_sms_alerts: false,
        mailing_address: {
          street_one: "",
          city: "",
          state: "",
          zip: ""
        },
        signup_url: window.location.href,
        referrer_url: "",
        password: data.password,
        source: "broker-invite"
      });
      await dispatch(
        loginUser({
          username: data.email,
          password: data.password,
          grant_type: "password",
          client_id: "yzxsFZJYZMxi5kLh21ZZklGYzSuu9zxNO6miNcN8"
        })
      );
      const paymentProfileId = (createdUser as CreatedInvitedUser)?.profiles?.payment?.id;
      if (!paymentProfileId) {
        showErrorMessage(
          "Your carrier profile was created, but its payment profile could not be loaded."
        );
        return;
      }
      handleNext(paymentProfileId);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.email?.[0] ||
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        "Error creating carrier profile.";
      showErrorMessage(errorMessage);
    }
  };

  return (
    <form className={styles.carrierInfoContainer} onSubmit={handleSubmit(handleCreateCarrier)}>
      <div className={styles.wrapper}>
        <div className={styles.enterMC}>
          <Input
            type="number"
            label={selectedCarrierType.value === "mc" ? "MC#" : "DOT#"}
            inputMode="numeric"
            name="identifyNumber"
            register={register}
            error={errors.identifyNumber}
            inputClass="!border-foreground h-12"
            className="flex-grow"
            onBlur={getFMCSAInfo}
          />
          <div className="flex !items-start pt-5">
            <Switch data={VEHICLE_NUMBER_TYPES} setSelectedValue={setSelectedCarrierType} />
          </div>
        </div>

        <div className="flex flex-col col-span-2 gap-2">
          <Input
            autoComplete="off"
            name="email"
            register={register}
            disabled={!!onboardingInformation?.email}
            error={errors.email}
            type="email"
            label="&nbsp;&nbsp;Email"
            onBlur={() => checkEmailExist(userEmail)}
          />
          {isDuplicatedEmail && (
            <span className="text-sm text-error">
              This email already exists. Please{" "}
              <Link to={routes.SIGNIN} className="text-base underline text-info underline-offset-4">
                Click Here
              </Link>{" "}
              to login and complete
            </span>
          )}
        </div>
        <Input
          autoComplete="off"
          name="password"
          error={errors.password}
          register={register}
          label="&nbsp;&nbsp;Password"
          isPasswordIcon
        />
        <Input
          autoComplete="off"
          name="confirm_password"
          error={errors.confirm_password}
          register={register}
          label="&nbsp;&nbsp;Confirm Password"
          isPasswordIcon
        />
        <Input
          name="owner_first_name"
          error={errors.owner_first_name}
          register={register}
          label="&nbsp;&nbsp;Owner’s First Name"
        />
        <Input
          name="owner_last_name"
          error={errors.owner_last_name}
          register={register}
          label="&nbsp;&nbsp;Owner’s Last Name"
        />
        <Controller
          name="owner_phone_number_cell"
          control={control}
          render={({ field: { value = "", onChange } }) => (
            <PhoneNumber
              value={value}
              onChange={onChange}
              placeholder="Phone Number"
              label="&nbsp;&nbsp;Owner’s Phone Number"
              disableCountryCode
              error={errors.owner_phone_number_cell}
              className="flex-1 col-span-2"
              wrapperClass="flex-1"
              data-cy="phonenumber-input"
            />
          )}
        />
        <Input
          name="company_name"
          error={errors.company_name}
          register={register}
          label="&nbsp;&nbsp;Company Name"
          className="col-span-2"
        />
        <Controller
          name="address"
          control={control}
          render={({ field: { onChange }, fieldState: { error } }) => (
            <GoogleAutoComplete
              label="Business Address"
              placeholder="Your Address"
              error={error}
              value={businessAddress}
              onChange={(addressInfo: AddressInfo) => onChange(addressInfo)}
              data-cy="signup-address-input"
              containerClass="col-span-2"
            />
          )}
        />

        <div className={styles.checkboxWrapper}>
          <Checkbox
            id="agree"
            name="agreement"
            register={register}
            error={errors.agreement}
            wrapperClass="text-sm">
            <span>I agree to the HaulPay </span>
            <a href={TERMS_OF_SERVICE} target="blank" className="underline text-primary">
              Terms of Service
            </a>
          </Checkbox>

          <Checkbox
            id="receive-message"
            name="receive_message"
            register={register}
            wrapperClass="text-sm"
            checkClass="!items-start">
            <span>I would like to receive </span>
            <Link to="" className="underline text-primary">
              Important messages regarding my operations.
            </Link>
          </Checkbox>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="outline" rounded size="lg" onClick={goBack}>
          CANCEL
        </Button>
        <Button type="submit" rounded size="lg" loading={isSubmitting}>
          NEXT
        </Button>
      </div>
    </form>
  );
}
